import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import LineItemsDialog from '@/components/common/LineItemsDialog';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { PERMISSIONS } from '@/types/auth';
import type { MaterialDto, PagedResult } from '@/types/materials';
import type { BomDto, BomLineDto, BomLineInput, CreateBomRequest, UpdateBomRequest } from '@/types/manufacturing';

const emptyLine: BomLineInput = { materialId: '', qty: 0, remarks: null };

export default function BomMaster() {
  const { t } = useTranslation('bomMaster');
  const queryClient = useQueryClient();

  const { data: boms = [], isLoading } = useQuery({
    queryKey: ['boms'],
    queryFn: async () => (await apiClient.get<BomDto[]>('/boms')).data,
  });
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  const materials = materialsPage?.items ?? [];
  // BOM eligibility is driven by the material's Category.Type — not a hardcoded list — see
  // MaterialCategory.Type's doc comment.
  const headerMaterials = materials.filter((m) => m.materialType === 'FG' || m.materialType === 'SFG');
  const componentMaterials = materials.filter((m) => m.materialType === 'RM' || m.materialType === 'SFG');

  const [open, setOpen] = useState(false);
  const [editingBom, setEditingBom] = useState<BomDto | null>(null);
  const [viewing, setViewing] = useState<BomDto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<BomDto | null>(null);

  const [materialId, setMaterialId] = useState('');
  const [baseQty, setBaseQty] = useState(1);
  const [status, setStatus] = useState<'Active' | 'Inactive'>('Active');
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState<BomLineInput[]>([{ ...emptyLine }]);

  const resetForm = () => {
    setMaterialId('');
    setBaseQty(1);
    setStatus('Active');
    setRemarks('');
    setLines([{ ...emptyLine }]);
  };

  const openCreate = () => {
    setEditingBom(null);
    resetForm();
    setOpen(true);
  };

  const openEdit = (bom: BomDto) => {
    setEditingBom(bom);
    setMaterialId(bom.materialId);
    setBaseQty(bom.baseQty);
    setStatus(bom.status as 'Active' | 'Inactive');
    setRemarks(bom.remarks ?? '');
    setLines(bom.lines.map((l) => ({ materialId: l.materialId, qty: l.qty, remarks: l.remarks })));
    setOpen(true);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['boms'] });

  const saveMutation = useMutation({
    mutationFn: () => {
      const cleanLines = lines.filter((l) => l.materialId);
      if (editingBom) {
        const payload: UpdateBomRequest = { baseQty, status, remarks: remarks || null, lines: cleanLines };
        return apiClient.put(`/boms/${editingBom.id}`, payload);
      }
      const payload: CreateBomRequest = { materialId, baseQty, remarks: remarks || null, lines: cleanLines };
      return apiClient.post('/boms', payload);
    },
    onSuccess: () => {
      invalidate();
      setOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/boms/${id}`),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
    },
  });

  const columns: DataTableColumn<BomDto>[] = [
    { field: 'bomNumber', headerName: t('table.number') },
    { field: 'materialCode', headerName: t('table.material'), render: (r) => `${r.materialCode} — ${r.materialName}` },
    { field: 'materialType', headerName: t('table.type'), render: (r) => r.materialType ?? '—', filterable: true },
    { field: 'baseQty', headerName: t('table.baseQty') },
    { field: 'lines', headerName: t('table.lines'), render: (r) => r.lines.length },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: t('table.actions'), render: (r) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={() => setViewing(r)}>
            {t('view')}
          </Button>
          <IfPermitted permission={PERMISSIONS.BOM_MANAGE}>
            <Button size="small" onClick={() => openEdit(r)}>
              {t('edit')}
            </Button>
            <Button size="small" color="error" onClick={() => setConfirmDelete(r)}>
              {t('delete')}
            </Button>
          </IfPermitted>
        </Stack>
      ),
    },
  ];

  const lineColumns: DataTableColumn<BomLineDto>[] = [
    { field: 'materialCode', headerName: t('table.material'), render: (l) => `${l.materialCode} — ${l.materialName}` },
    { field: 'materialType', headerName: t('table.type'), render: (l) => l.materialType ?? '—' },
    { field: 'qty', headerName: t('table.qty') },
    { field: 'remarks', headerName: t('form.remarks'), render: (l) => l.remarks ?? '—' },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.BOM_MANAGE}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              {t('addBom')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={boms} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="boms" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingBom ? editingBom.bomNumber : t('addBom')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <SearchableSelect
                label={t('form.headerMaterial')} value={materialId} onChange={setMaterialId}
                fullWidth disabled={Boolean(editingBom)} helperText={t('form.headerMaterialHint')}
                options={headerMaterials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name} (${m.materialType})` }))}
              />
              <TextField
                type="number" label={t('form.baseQty')} value={baseQty} fullWidth
                onChange={(e) => setBaseQty(Number(e.target.value))}
              />
              {editingBom && (
                <TextField select label={t('form.status')} value={status} onChange={(e) => setStatus(e.target.value as 'Active' | 'Inactive')} fullWidth>
                  <MenuItem value="Active">{t('form.statusActive')}</MenuItem>
                  <MenuItem value="Inactive">{t('form.statusInactive')}</MenuItem>
                </TextField>
              )}
            </Stack>
            <TextField label={t('form.remarks')} value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth multiline minRows={2} />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <strong>{t('form.components')}</strong>
            </Stack>

            {lines.map((line, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="center">
                <SearchableSelect
                  label={t('form.componentMaterial')} value={line.materialId} fullWidth size="small"
                  onChange={(materialId) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, materialId } : l)))}
                  options={componentMaterials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name} (${m.materialType})` }))}
                />
                <TextField
                  type="number" label={t('form.qty')} value={line.qty} size="small" sx={{ minWidth: 120 }}
                  onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, qty: Number(e.target.value) } : l)))}
                />
                <IconButton size="small" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} disabled={lines.length <= 1}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setLines((prev) => [...prev, { ...emptyLine }])}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('form.addLine')}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('discardChanges')}</Button>
          <Button
            variant="contained"
            disabled={!materialId || lines.some((l) => !l.materialId || l.qty <= 0) || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {t('saveBom')}
          </Button>
        </DialogActions>
      </Dialog>

      <LineItemsDialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing?.bomNumber ?? ''}
        subtitle={viewing ? `${viewing.materialCode} — ${viewing.materialName} (${t('form.baseQty')}: ${viewing.baseQty})` : undefined}
        columns={lineColumns}
        rows={viewing?.lines ?? []}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        message={t('deleteBom.message')}
        loading={deleteMutation.isPending}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onClose={() => setConfirmDelete(null)}
      />
    </>
  );
}
